(ns web3.modals.alerts
  (:require [reagent.core :as r]
            [web3.modals.core :as modal]
            [re-frame-simple.db :as db]
            [web3.i18n :as i]
            [web3.utils :as utils]
            [web3.persistence.graphql :as g]
            [web3.components.widgets :as widgets]
            [web3.constants :as constants]
            [web3.log :as log]
            [web3.view :as v]))

;; ========== Modal Alerts ==========
;;
;; This is a stack-based modal, suitable for ephemeral messages which
;; are not reachable by a URL.
;;
;; USAGE

(comment

 ;; show an alert
 (add-frame [alert-view {:text-primary "Title text"
                         :text-secondary "Sub text"
                         :buttons [{:label "Action text"
                                    :on-click #(do
                                                 (drop-frames 1)
                                                 (log/info "Test"))}]}])

 ;; alerts are a stack, so you can add an alert "on top" of another alert.

 ;; hide the topmost alert
 (drop-frames 1)
 ;; hide the top 2 alerts at once
 (drop-frames 2))

(defn add-frame [content]
  (db/dispatch [:update :modal-alerts/frames conj {:content content}]))

(defn drop-frames
  ([] (drop-frames 1))
  ([n]
   ;; augment props of current frame with `:will-unmount?` key to trigger fade-out
   (db/dispatch [:update :modal-alerts/frames (fn [frames]
                                                (when (seq frames)
                                                  (cons (assoc (first frames)
                                                          :will-unmount? true)
                                                        (rest frames))))])
   ;; delay dropping frame(s) until transition is complete
   (-> #(db/dispatch [:update :modal-alerts/frames (partial drop n)])
       (js/setTimeout 200))
   true))

(v/defview ^:private transitioned-dialog
  {:get-initial-state
   (fn [] {:transition-state :will-mount})
   :component-will-receive-props
   (fn [{state :state
         [_ _ element] :argv} [_ next-props next-element]]
     (cond
       (:will-unmount? next-props)
       (swap! state assoc :transition-state :will-unmount)

       ;; if element has changed, treat the same as a newly mounted component
       (not= element next-element)
       (swap! state assoc :transition-state :mounted)))
   :component-did-mount
   (fn [{:keys [state]}]
     (swap! state assoc :transition-state :mounted))}
  [props element]
  (let [{:keys [state]} (r/current-component)
        {:keys [transition-state]} @state]
    [:div.flex.items-center.justify-center
     {:style
      (merge {:transition "0.2s ease transform, 0.2s ease opacity"}
             (case transition-state
               (:will-mount
                :will-unmount) {:transform "scale(0.8)"
                                :opacity 0}
               :mounted {:transform "scale(1)"
                         :opacity 1}))}
     element]))

(defn alert-frames
  "The root alerts view, responsible for showing whatever alerts are currently active.

  Should be included once somewhere in the app to enable alerts."
  [{:keys [z-index modal/closable?]
    :or {closable? true}}]
  (let [{:keys [content will-unmount?] :as frame} (first (db/get :modal-alerts/frames))]
    (when content
      [modal/modal-container
       (-> frame
           (dissoc :will-unmount?)
           (merge {:key "modal/frames"
                   :class "modal-container"
                   :on-close (when closable?
                               #(drop-frames 1))
                   :z-index z-index}))
       [transitioned-dialog {:will-unmount? will-unmount?}
        content]])))




(defn alert-view
  "Presents a formatted alert box, to be used like so: (add-frame [alert-view {...}]).

   If mutation-state is provided, it will be displayed above the button bar."
  [{:keys [text-primary
           text-secondary
           buttons
           mutation-state]}]
  (let [{:keys [graphql/loading?]} mutation-state]
    [:div.modal-content.round-3.bg-white.shadow-modal.overflow-y-auto.modal-alert.overflow-x-hidden.flex.flex-column.items-stretch.border-white
     {:style {:z-index (inc (:dialog constants/z-index))}}
     [:div.text-center.flex-auto.flex.items-center.flex-column.justify-center
      {:style {:padding "15px 25px"}
       :class (when loading? "opacity-50")}
      [:div.bg-white.pad-all-3
       [:div.header-4.margin-v-2 (utils/eval-if-fn text-primary)]
       [:div.text-body.margin-v-2 (utils/eval-if-fn text-secondary)]]]
     (when mutation-state
       [widgets/graphql-state mutation-state])
     (modal/modal-buttons buttons)]))

(defn ^:private delete-confirmation [{:keys [mutate!
                                             on-error
                                             on-complete
                                             text-primary
                                             text-secondary] :as props}]
  (let [this (r/current-component)
        {:keys [graphql/loading?] :as mutation-state} (g/mutation-state this)]
    [alert-view {:text-primary (utils/eval-if-fn (or text-primary (i/get :modal/delete.primary)))
                 :text-secondary (utils/eval-if-fn (or text-secondary (i/get :modal/delete.info)))
                 :mutation-state mutation-state
                 :buttons [{:label (i/get :i/cancel)
                            :on-click #(drop-frames 2)}
                           {:label (i/get :i/delete)
                            :color "red"
                            :on-click #(when-not loading?
                                         (mutate! {:component this
                                                   :on-error on-error
                                                   :on-complete (fn []
                                                                  (drop-frames 2)
                                                                  (when on-complete (on-complete)))}))}]}]))

(defn delete-with-confirmation [{:keys [text-primary
                                        text-secondary
                                        confirm] :as props}]
  (let [confirm! #(add-frame [delete-confirmation (-> props
                                                      (select-keys [:mutate!
                                                                    :on-complete
                                                                    :on-error])
                                                      (merge confirm))])]
    [alert-view {:text-primary (utils/eval-if-fn text-primary)
                 :text-secondary (utils/eval-if-fn text-secondary)
                 :buttons [{:label (i/get :i/cancel)
                            :on-click #(drop-frames 1)}
                           {:label (i/get :i/delete)
                            :on-click (fn []
                                        (drop-frames 1)
                                        (js/setTimeout confirm! 200))}]}]))

(defn property-transfer-withdraw
  "For the case of using an alert to trigger a mutation, with a cancel button and an 'action' button.

  :mutate! should be a graphql mutation with its 'data' already partially applied.

  :on-complete and :on-error will be passed as options to `mutate!`.

  When the mutation is complete, the alert will hide itself."
  [{:keys [mutate!
           on-error
           on-complete
           text-action] :as props}]
  (let [this (r/current-component)
        {:as mutation-state
         :keys [graphql/loading?
                graphql/error]} (g/mutation-state this)]
    [alert-view (-> props
                    (dissoc :text-action :mutate! :on-complete :on-error)
                    (assoc
                      :mutation-state mutation-state
                      :buttons (cond-> [{:label (i/get :i/cancel)
                                         :on-click #(drop-frames 2)}]
                                       (not error) (conj {:label text-action
                                                          :on-click #(when-not loading?
                                                                       (mutate! {:component this
                                                                                 :on-error on-error
                                                                                 :on-complete (fn []
                                                                                                (drop-frames 2)
                                                                                                (when on-complete (on-complete)))}))}))))]))